import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Row, Col, Spinner, Alert, Table } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import Select from 'react-select';
import api from '../helpers/api';

/**
 * Create / edit form for a Purchase Order. Vendor block can be picked from the directory
 * (snapshotted) or typed free-text; line items are dynamic with optional inventory linking;
 * totals are computed live for display (the server recomputes them authoritatively).
 *
 * Props: isOpen, toggle, poData (edit), prefill (from a request), onSubmit(payload),
 *        isSubmitting, apiError, orgCurrency, defaultTaxRate, enumData
 */
const emptyLine = (taxRate) => ({
  itemType: 'FREE_TEXT', itemId: null, reference: '', description: '',
  quantity: 1, unit: '', unitPrice: 0, taxRate: taxRate ?? 0,
});

const PurchaseOrderModal = ({
  isOpen, toggle, poData, prefill, onSubmit, isSubmitting, apiError = '',
  orgCurrency = '', defaultTaxRate = 0, enumData = [],
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({});
  const [lines, setLines] = useState([emptyLine(defaultTaxRate)]);
  const [vendors, setVendors] = useState([]);
  const [spareParts, setSpareParts] = useState([]);
  const [errors, setErrors] = useState({});

  const currentUser = JSON.parse(localStorage.getItem('authUser'));
  const organizationId = currentUser?.activeOrganizationId ?? null;
  const isEdit = !!poData?.id;

  const selectStyles = {
    control: (b, s) => ({ ...b, minHeight: 34, borderColor: s.isFocused ? '#86b7fe' : '#ced4da', boxShadow: 'none' }),
    option: (b, s) => ({ ...b, backgroundColor: s.isSelected || s.isFocused ? '#3980c0' : 'white', color: s.isSelected || s.isFocused ? 'white' : '#212529' }),
    // The item picker portals its menu to <body>; without this the menu opens BELOW the
    // Bootstrap modal (z-index 1055) and looks like the dropdown does nothing.
    menuPortal: (b) => ({ ...b, zIndex: 9999 }),
  };

  const unitOptions = (enumData?.find((e) => e.name === 'unit')?.values || []).map((v) => ({ value: v, label: v }));
  // Line types mirror the request-type vocabulary: Asset | Parts & Consumables | Free text.
  // SPARE_PART is the enum value behind "Parts & Consumables" (the unified inventory);
  // ASSET lines register received units as assets; FREE_TEXT covers services/one-offs.
  const typeOptions = [
    { value: 'ASSET', label: t('planning_purchasing.type_asset', 'Asset') },
    { value: 'SPARE_PART', label: t('planning_purchasing.type_parts_consumables', 'Parts & Consumables') },
    { value: 'FREE_TEXT', label: t('planning_purchasing.po_line_free_text', 'Free text') },
  ];

  // Load reference data — each list is independent so one failing endpoint
  // doesn't break the form.
  useEffect(() => {
    if (!isOpen || !organizationId) return;
    api.get('vendor/getAllVendors?count=9999')
      .then((v) => setVendors(v.data?.response || []))
      .catch((e) => console.error('vendors load error', e));
    api.get(`spare-parts/all?organizationId=${organizationId}&count=9999`)
      .then((sp) => setSpareParts(sp.data?.response?.[0]?.spareParts || sp.data?.response || []))
      .catch((e) => console.error('spare parts load error', e));
  }, [isOpen, organizationId]);

  // Init form when opened
  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    if (poData?.id) {
      setForm({ ...poData });
      setLines((poData.lineItems || []).map((li) => ({
        id: li.id, itemType: li.itemType || 'FREE_TEXT', itemId: li.itemId ?? null,
        reference: li.reference || '', description: li.description || '',
        quantity: li.quantity ?? 1, unit: li.unit || '', unitPrice: li.unitPrice ?? 0, taxRate: li.taxRate ?? 0,
      })) || [emptyLine(defaultTaxRate)]);
    } else {
      setForm({
        poNumber: '', issueDate: new Date().toISOString().slice(0, 10),
        procurementRequestId: prefill?.procurementRequestId ?? null,
        procurementRequestNumber: prefill?.requestNumber ?? null,
        procurementRequestTitle: prefill?.requestTitle ?? null,
        vendorId: prefill?.vendorId ?? null, vendorName: prefill?.vendorName || '', vendorAddress: '', vendorCompanyIdNumber: '',
        vendorContactName: '', vendorContactEmail: '', vendorContactPhone: '',
        deliveryAddress: prefill?.deliveryAddress || '', paymentTerms: '', budgetCode: '',
        notes: prefill?.notes || '',
        currency: prefill?.currency || orgCurrency || '',
      });
      setLines(prefill?.lines?.length
        ? prefill.lines.map((l) => ({ ...emptyLine(defaultTaxRate), ...l }))
        : [emptyLine(defaultTaxRate)]);
    }
  }, [isOpen, poData, prefill, orgCurrency, defaultTaxRate]);

  // When a prefill carried a directory vendor id (request's suggested vendor),
  // snapshot the vendor block as soon as the directory list arrives.
  useEffect(() => {
    if (!isOpen || !form.vendorId || form.vendorAddress || isEdit) return;
    const v = vendors.find((x) => x.id === form.vendorId);
    if (v) {
      setForm((p) => ({
        ...p, vendorName: v.vendorName || p.vendorName, vendorAddress: v.address || '',
        vendorCompanyIdNumber: v.companyIdNumber || '', vendorContactEmail: v.email || '',
        vendorContactPhone: v.phoneNumber || '',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendors, isOpen, form.vendorId]);

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));
  const setLine = (idx, field, value) => setLines((p) => p.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  const addLine = () => setLines((p) => [...p, emptyLine(defaultTaxRate)]);
  const removeLine = (idx) => setLines((p) => (p.length > 1 ? p.filter((_, i) => i !== idx) : p));

  // When an inventory item is picked, prefill description + unit price
  const pickItem = (idx, itemType, option) => {
    if (!option) { setLine(idx, 'itemId', null); return; }
    setLines((p) => p.map((l, i) => i === idx ? {
      ...l, itemId: option.value,
      description: l.description || option.label,
      unitPrice: (l.unitPrice && l.unitPrice > 0) ? l.unitPrice : (option.price ?? 0),
    } : l));
  };

  const sparePartOptions = spareParts.map((s) => ({ value: s.id, label: s.name || s.sparePartName || `#${s.id}`, price: s.purchasePrice ?? s.price ?? 0 }));
  const vendorOptions = vendors.map((v) => ({ value: v.id, label: v.vendorName, raw: v }));

  const onVendorPick = (opt) => {
    if (!opt) { set('vendorId', null); return; }
    const v = opt.raw;
    setForm((p) => ({
      ...p, vendorId: v.id, vendorName: v.vendorName || '', vendorAddress: v.address || '',
      vendorCompanyIdNumber: v.companyIdNumber || '', vendorContactEmail: v.email || '',
      vendorContactPhone: v.phoneNumber || '',
    }));
  };

  const totals = useMemo(() => {
    let subtotal = 0, tax = 0;
    lines.forEach((l) => {
      const lt = (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0);
      subtotal += lt;
      tax += lt * (Number(l.taxRate) || 0) / 100;
    });
    return { subtotal, tax, total: subtotal + tax };
  }, [lines]);

  const fmt = (n) => (Number(n) || 0).toFixed(2);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    const errs = {};
    if (!form.vendorName?.trim()) errs.vendorName = t('planning_purchasing.vendor_required', 'Vendor name is required');
    const validLines = lines.filter((l) => (l.description?.trim() || l.itemId) && Number(l.quantity) > 0);
    if (validLines.length === 0) errs.lines = t('planning_purchasing.line_required', 'At least one line item is required');
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const payload = {
      ...(poData?.id ? { id: poData.id } : {}),
      procurementRequestId: form.procurementRequestId ?? null,
      poNumber: form.poNumber || null,
      issueDate: form.issueDate || null,
      vendorId: form.vendorId ?? null,
      vendorName: form.vendorName, vendorAddress: form.vendorAddress || null,
      vendorCompanyIdNumber: form.vendorCompanyIdNumber || null,
      vendorContactName: form.vendorContactName || null,
      vendorContactEmail: form.vendorContactEmail || null,
      vendorContactPhone: form.vendorContactPhone || null,
      deliveryAddress: form.deliveryAddress || null, paymentTerms: form.paymentTerms || null,
      budgetCode: form.budgetCode || null, notes: form.notes || null,
      currency: form.currency || null,
      lineItems: validLines.map((l, i) => ({
        id: l.id, itemType: l.itemType || 'FREE_TEXT', itemId: l.itemType === 'SPARE_PART' ? (l.itemId ?? null) : null,
        reference: l.reference || null, description: l.description || null,
        quantity: Number(l.quantity) || 0, unit: l.unit || null,
        unitPrice: Number(l.unitPrice) || 0, taxRate: Number(l.taxRate) || 0, sortOrder: i,
      })),
    };
    onSubmit(payload);
  }, [form, lines, poData, onSubmit, t]);

  return (
    <Modal size="xl" isOpen={isOpen} toggle={toggle}>
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2 }}>
        <form onSubmit={handleSubmit} noValidate>
          <div className="modal-header text-white">
            <h5 className="modal-title fs-4">
              {isEdit ? t('planning_purchasing.edit_po', 'Edit Purchase Order') : t('planning_purchasing.new_po', 'New Purchase Order')}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={toggle} />
          </div>
          <div className="modal-body p-4">
            {apiError && <Alert color="danger">{apiError}</Alert>}
            {errors.lines && <Alert color="warning">{errors.lines}</Alert>}

            {/* Make the request link visible — the prefill is otherwise easy to miss,
                especially on parts lines where the title sits behind the item picker. */}
            {form.procurementRequestId && (
              <Alert color="info" className="py-2 d-flex align-items-center gap-2">
                <FeatherIcon icon="link" size={14} />
                <span>
                  {t('planning_purchasing.created_from_request', 'Created from request')}{' '}
                  <strong>{form.procurementRequestNumber || `#${form.procurementRequestId}`}</strong>
                  {form.procurementRequestTitle ? <> — {form.procurementRequestTitle}</> : null}
                </span>
              </Alert>
            )}

            {/* Header */}
            <Row className="mb-3">
              <Col md={4}>
                <label className="form-label fw-medium">{t('planning_purchasing.po_number', 'PO Number')}</label>
                <input className="form-control" value={form.poNumber || ''} onChange={(e) => set('poNumber', e.target.value)}
                       placeholder={t('planning_purchasing.auto_generated', 'Auto-generated')} />
              </Col>
              <Col md={4}>
                <label className="form-label fw-medium">{t('planning_purchasing.issue_date', 'Issue Date')}</label>
                <input type="date" className="form-control" value={form.issueDate || ''} onChange={(e) => set('issueDate', e.target.value)} />
              </Col>
              <Col md={4}>
                <label className="form-label fw-medium">{t('planning_purchasing.currency', 'Currency')}</label>
                <input className="form-control text-uppercase" maxLength={3} value={form.currency || ''} onChange={(e) => set('currency', e.target.value.toUpperCase())} />
              </Col>
            </Row>

            {/* Vendor block */}
            <div className="border rounded p-3 mb-3 bg-light">
              <h6 className="text-primary mb-3">{t('planning_purchasing.vendor', 'Vendor')}</h6>
              <Row className="mb-2">
                <Col md={6}>
                  <label className="form-label small">{t('planning_purchasing.select_vendor', 'Select from directory')}</label>
                  <Select options={vendorOptions} value={vendorOptions.find((o) => o.value === form.vendorId) || null}
                          onChange={onVendorPick} isClearable placeholder={t('planning_purchasing.select_vendor', 'Select from directory')}
                          classNamePrefix="react-select" styles={selectStyles} />
                </Col>
                <Col md={6}>
                  <label className="form-label small">{t('planning_purchasing.vendor_name', 'Vendor Name')} <span className="text-danger">*</span></label>
                  <input className={`form-control ${errors.vendorName ? 'is-invalid' : ''}`} value={form.vendorName || ''} onChange={(e) => set('vendorName', e.target.value)} />
                  {errors.vendorName && <small className="text-danger">{errors.vendorName}</small>}
                </Col>
              </Row>
              <Row className="mb-2">
                <Col md={6}><label className="form-label small">{t('shared.address', 'Address')}</label>
                  <input className="form-control" value={form.vendorAddress || ''} onChange={(e) => set('vendorAddress', e.target.value)} /></Col>
                <Col md={6}><label className="form-label small">{t('planning_purchasing.company_id', 'Company ID')}</label>
                  <input className="form-control" value={form.vendorCompanyIdNumber || ''} onChange={(e) => set('vendorCompanyIdNumber', e.target.value)} /></Col>
              </Row>
              <Row>
                <Col md={4}><label className="form-label small">{t('planning_purchasing.contact_name', 'Contact Name')}</label>
                  <input className="form-control" value={form.vendorContactName || ''} onChange={(e) => set('vendorContactName', e.target.value)} /></Col>
                <Col md={4}><label className="form-label small">{t('shared.email', 'Email')}</label>
                  <input className="form-control" value={form.vendorContactEmail || ''} onChange={(e) => set('vendorContactEmail', e.target.value)} /></Col>
                <Col md={4}><label className="form-label small">{t('planning_purchasing.phone', 'Phone')}</label>
                  <input className="form-control" value={form.vendorContactPhone || ''} onChange={(e) => set('vendorContactPhone', e.target.value)} /></Col>
              </Row>
            </div>

            {/* Line items */}
            <h6 className="text-primary mb-2">{t('planning_purchasing.line_items', 'Line Items')}</h6>
            <div className="table-responsive mb-2">
              <Table size="sm" bordered className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ minWidth: 130 }}>{t('planning_purchasing.type', 'Type')}</th>
                    <th style={{ minWidth: 220 }}>{t('shared.description', 'Description')}</th>
                    <th style={{ width: 90 }}>{t('planning_purchasing.ref', 'Ref')}</th>
                    <th style={{ width: 70 }}>{t('planning_purchasing.qty', 'Qty')}</th>
                    <th style={{ minWidth: 110 }}>{t('planning_purchasing.unit', 'Unit')}</th>
                    <th style={{ width: 100 }}>{t('planning_purchasing.unit_price', 'Unit Price')}</th>
                    <th style={{ width: 70 }}>{t('planning_purchasing.tax_pct', 'Tax %')}</th>
                    <th style={{ width: 100 }} className="text-end">{t('planning_purchasing.line_total', 'Total')}</th>
                    <th style={{ width: 36 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, idx) => {
                    const lineTotal = (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0);
                    const itemOpts = l.itemType === 'SPARE_PART' ? sparePartOptions : [];
                    return (
                      <tr key={idx}>
                        <td>
                          {/* Native selects in the dense line table — react-select gets crushed
                              in narrow cells and truncates its own placeholder. */}
                          <select className="form-select form-select-sm" value={l.itemType}
                                  onChange={(e) => { setLine(idx, 'itemType', e.target.value); setLine(idx, 'itemId', null); }}>
                            {typeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </td>
                        <td>
                          {/* Only Parts & Consumables lines link to inventory; Asset and
                              Free-text lines take a typed description. */}
                          {l.itemType === 'SPARE_PART' ? (
                            <Select options={itemOpts} value={itemOpts.find((o) => o.value === l.itemId) || null}
                                    onChange={(o) => pickItem(idx, l.itemType, o)} isClearable
                                    placeholder={l.description || t('planning_purchasing.pick_item', 'Pick item...')}
                                    classNamePrefix="react-select" styles={selectStyles} menuPortalTarget={document.body} />
                          ) : (
                            <input className="form-control form-control-sm" value={l.description} onChange={(e) => setLine(idx, 'description', e.target.value)} />
                          )}
                        </td>
                        <td><input className="form-control form-control-sm" value={l.reference} onChange={(e) => setLine(idx, 'reference', e.target.value)} /></td>
                        <td><input type="number" min="0" className="form-control form-control-sm" value={l.quantity} onChange={(e) => setLine(idx, 'quantity', e.target.value)} /></td>
                        <td>
                          <select className="form-select form-select-sm" value={l.unit || ''}
                                  onChange={(e) => setLine(idx, 'unit', e.target.value)}>
                            <option value="">{t('planning_purchasing.select_unit', 'Select unit')}</option>
                            {unitOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </td>
                        <td><input type="number" min="0" step="0.01" className="form-control form-control-sm" value={l.unitPrice} onChange={(e) => setLine(idx, 'unitPrice', e.target.value)} /></td>
                        <td><input type="number" min="0" step="0.01" className="form-control form-control-sm" value={l.taxRate} onChange={(e) => setLine(idx, 'taxRate', e.target.value)} /></td>
                        <td className="text-end">{fmt(lineTotal)}</td>
                        <td className="text-center">
                          <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={() => removeLine(idx)}><FeatherIcon icon="x" size={16} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
            <button type="button" className="btn btn-sm btn-outline-primary mb-3" onClick={addLine}>
              <FeatherIcon icon="plus" size={14} className="me-1" />{t('planning_purchasing.add_line', 'Add Line')}
            </button>

            {/* Totals */}
            <Row className="mb-3">
              <Col md={{ size: 4, offset: 8 }}>
                <div className="d-flex justify-content-between"><span>{t('planning_purchasing.subtotal', 'Subtotal')}</span><span>{fmt(totals.subtotal)} {form.currency}</span></div>
                <div className="d-flex justify-content-between"><span>{t('planning_purchasing.tax', 'Tax')}</span><span>{fmt(totals.tax)} {form.currency}</span></div>
                <div className="d-flex justify-content-between fw-bold border-top pt-1"><span>{t('planning_purchasing.total', 'Total')}</span><span>{fmt(totals.total)} {form.currency}</span></div>
              </Col>
            </Row>

            {/* Footer fields */}
            <Row className="mb-2">
              <Col md={6}><label className="form-label small">{t('planning_purchasing.delivery_address', 'Delivery Address')}</label>
                <input className="form-control" value={form.deliveryAddress || ''} onChange={(e) => set('deliveryAddress', e.target.value)} /></Col>
              <Col md={3}><label className="form-label small">{t('planning_purchasing.payment_terms', 'Payment Terms')}</label>
                <input className="form-control" value={form.paymentTerms || ''} onChange={(e) => set('paymentTerms', e.target.value)} placeholder="Net 30" /></Col>
              <Col md={3}><label className="form-label small">{t('planning_purchasing.budget_code', 'Budget Code')}</label>
                <input className="form-control" value={form.budgetCode || ''} onChange={(e) => set('budgetCode', e.target.value)} /></Col>
            </Row>
            <Row>
              <Col md={12}><label className="form-label small">{t('shared.notes', 'Notes')}</label>
                <textarea className="form-control" rows={2} value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} />
                <small className="text-muted">{t('planning_purchasing.notes_visible_hint', 'Visible to the vendor — printed on the PO document.')}</small></Col>
            </Row>
          </div>
          <div className="modal-footer bg-light border-top-0">
            <button type="button" className="btn btn-outline-secondary" onClick={toggle}>{t('shared.cancel', 'Cancel')}</button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? <><Spinner size="sm" className="me-2" />{t('shared.saving', 'Saving...')}</>
                : (isEdit ? t('planning_purchasing.update_po', 'Update PO') : t('planning_purchasing.create_po', 'Create PO'))}
            </button>
          </div>
        </form>
      </motion.div>
    </Modal>
  );
};

export default PurchaseOrderModal;
